import {forwardRef, useId, type ComponentPropsWithoutRef, type ReactNode} from 'react';
import {cn} from '../lib/utils';
import {FieldLabel, FieldMessage} from '../lib/field';
import type {Testable} from '../lib/testable';
import {Stack} from '../Stack';
import {Success1Icon} from '../icons';

export interface CheckboxProps extends Omit<ComponentPropsWithoutRef<'input'>, 'type' | 'className'>, Testable {
	label?: ReactNode;
	error?: ReactNode;
	hint?: ReactNode;
	required?: boolean;
	indeterminate?: boolean;
	className?: string;
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(({
	label,
	error,
	hint,
	required = false,
	indeterminate = false,
	disabled = false,
	id,
	testId,
	className,
	...props
}, ref) => {
	const generatedId = useId();
	const inputId = id ?? generatedId;
	const messageId = `${inputId}-message`;
	const message = error ?? hint;
	const border = error ? 'border-tone-red' : hoverBorderStyles;

	const setRefs = (el: HTMLInputElement | null) => {
		if (el) el.indeterminate = indeterminate;
		if (typeof ref === 'function') ref(el);
		else if (ref) (ref as {current: HTMLInputElement | null}).current = el;
	};

	return (
		<div data-component='CheckboxField' className={cn(className, 'flex flex-col')}>
			<Stack as='label' htmlFor={inputId} className={rowStyles}>
				<span className='relative inline-flex'>
					<input
						ref={setRefs}
						id={inputId}
						type='checkbox'
						data-component='Checkbox'
						data-testid={testId}
						disabled={disabled}
						aria-required={required || undefined}
						aria-invalid={error ? true : undefined}
						aria-describedby={message ? messageId : undefined}
						className={cn(boxStyles, border)}
						{...props}/>
					{indeterminate
						? <span aria-hidden className={dashStyles}/>
						: <Success1Icon aria-hidden size='xs' className={checkStyles}/>}
				</span>
				{label && <FieldLabel required={required} disabled={disabled}>{label}</FieldLabel>}
			</Stack>
			{message && <FieldMessage id={messageId} error={Boolean(error)}>{message}</FieldMessage>}
		</div>
	);
});

Checkbox.displayName = 'Checkbox';

const rowStyles = 'w-fit cursor-pointer has-[:disabled]:cursor-not-allowed';

const boxStyles =
	'peer size-4 shrink-0 cursor-pointer appearance-none rounded border border-foreground-300 bg-transparent ' +
	'transition-colors checked:border-brand-main checked:bg-brand-main checked:shadow-s ' +
	'indeterminate:border-brand-main indeterminate:bg-brand-main indeterminate:shadow-s ' +
	'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-main-light ' +
	'disabled:cursor-not-allowed disabled:border-foreground-300 disabled:bg-background-500 disabled:shadow-none';

// Tailwind emits checked and indeterminate before hover, so a bare hover:border-* would win over the
// brand border on a box that is already ticked. The guards, not the variant order, decide this one.
const hoverBorderStyles = '[&:hover:not(:checked):not(:indeterminate):not(:disabled)]:border-foreground-200';

const checkStyles =
	'pointer-events-none absolute inset-0 m-auto hidden text-foreground-white ' +
	'peer-checked:block peer-disabled:text-foreground-200';

const dashStyles =
	'pointer-events-none absolute left-1/2 top-1/2 h-[1.5px] w-[7px] -translate-x-1/2 -translate-y-1/2 ' +
	'bg-foreground-white peer-disabled:bg-foreground-200';
